import { useFormik } from "formik";
import React, { useState } from "react";
import { useCookies } from "react-cookie";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup"; // Import Yup for validation
import logo from "../../../images/logo.svg";
import { postApiCaller } from "../../../libs/apiCaller";

const Login = () => {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const navigate = useNavigate();
  const [, setCookie] = useCookies(["reebukToken"]);

  // ✅ Formik & Yup Validation
  const formik = useFormik({
    initialValues: {
      user_name: "",
      password: "",
    },
    validationSchema: Yup.object({
      user_name: Yup.string()
        // .email("Invalid email address")
        .required("User Name is required"),
      password: Yup.string()
        .required("Password is required")
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
          "Password must have at least 1 uppercase, 1 lowercase, 1 number, and 1 special character"
        ),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      const response = await postApiCaller("login", values);
      if (response?.data.response_code === 200) {
        const token = response.data.response_data.token;
        // Set cookie with 8-hour expiration
        setCookie("reebukToken", token, {
          path: "/",
          maxAge: 8 * 60 * 60, // 8 hours in seconds
          secure: true, // Ensures it's only sent over HTTPS
          httpOnly: false, // Set to true if you want it to be accessible only via the server
          sameSite: "Strict",
        });
        toast.success(response.data.response_message);
        navigate("/dashboard");
        setTimeout(() => {
          window.location.reload();
        }, 100);
      } else {
        toast.error(response?.data?.response_message);
      }
      setSubmitting(false);
    },
  });

  return (
    <div className="h-screen grid grid-cols-1 md:grid-cols-2">
      <div className="bg-[#2841840D] hidden md:block">
        <div className="flex h-screen items-center justify-center">
          <p className="text-2xl">Reebuk Health</p>
        </div>
      </div>
      <div className="flex items-center justify-center">
        <div className="p-4 md:p-8 lg:p-16 w-full">
          <img src={logo} alt="Reebuk Logo" />
          <div className="my-6">
            <p className="text-2xl font-semibold text-[#284184]">Welcome 👋</p>
            <p className="text-[#8B8D98]">Please login here</p>
          </div>
          <form onSubmit={formik.handleSubmit}>
            {/* Email Field */}
            <div>
              {/* <label className="mb-2.5 block font-medium">Email</label>
              <input
                type="email"
                placeholder="Enter your email"
                className="w-full rounded-lg border border-stroke bg-transparent py-3 px-3 outline-none focus:border-primary"
                {...formik.getFieldProps("email")}
              />
              {formik.touched.email && formik.errors.email && (
                <p className="text-red-500 text-sm">{formik.errors.email}</p>
              )} */}

              <label className="mb-2.5 block font-medium">User Name</label>
              <input
                type="text"
                placeholder="Enter User Name"
                className="w-full rounded-lg border border-stroke bg-transparent py-3 px-3 outline-none focus:border-primary"
                {...formik.getFieldProps("user_name")}
              />
              {formik.touched.user_name && formik.errors.user_name && (
                <p className="text-red-500 text-sm">
                  {formik.errors.user_name}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div className="my-4">
              <label className="mb-2.5 block font-medium">Password</label>
              <div className="relative">
                <input
                  type={passwordVisible ? "text" : "password"}
                  placeholder="Enter your password"
                  className="w-full rounded-lg border border-gray-300 bg-transparent py-4 pl-6 pr-10 outline-none"
                  {...formik.getFieldProps("password")}
                />
                <button
                  type="button"
                  onClick={() => setPasswordVisible(!passwordVisible)}
                  className="absolute top-1/2 right-4 -translate-y-1/2 cursor-pointer"
                >
                  {passwordVisible ? (
                    <FaEye className="text-xl" />
                  ) : (
                    <FaEyeSlash className="text-xl" />
                  )}
                </button>
              </div>
              {formik.touched.password && formik.errors.password && (
                <p className="text-red-500 text-sm">{formik.errors.password}</p>
              )}
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex justify-between mb-10">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <label htmlFor="remember-me" className="ml-2 text-sm">
                  Remember Me
                </label>
              </div>
              <Link
                to="/auth/forgotPassword"
                className="text-[#284184] hover:underline"
              >
                Forgot Password?
              </Link>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full font-bold bg-[#284184] text-white py-3 rounded-xl"
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting ? "Logging in..." : "Login"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
